
import React, { createContext, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNotifications } from './NotificationContext';
import { useMarketing } from './MarketingContext';
import { activityService, SocialProofEvent } from '../domain/services/activityService';

const SocialProofContext = createContext(null);

export const SocialProofProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [activeEvent, setActiveEvent] = useState<SocialProofEvent | null>(null);
    const displayTimeoutRef = useRef<number | null>(null);
    const { addNotification } = useNotifications();
    const { socialProof } = useMarketing();

    // Subscribe to Activity Stream (Firestore Simulation)
    useEffect(() => {
        if (!socialProof.enabled) return;

        // Use custom intervals from config
        const unsubscribe = activityService.subscribeToActivity((event) => {
            const sensitivePaths = ['/payment', '/login', '/register-success', '/checkout-review'];
            const currentPath = window.location.hash.replace('#', '');
            
            if (sensitivePaths.includes(currentPath)) return;

            // 1. Show Toast
            setActiveEvent(event);

            // 2. Add to Notification Center
            addNotification({
                title: 'Actividad en vivo',
                message: `${event.user} de ${event.location} ${event.action} ${event.productName}.`,
                type: 'social',
                linkTo: `/product/${event.productId}`
            });

            // 3. Hide Toast after 5s
            if (displayTimeoutRef.current) clearTimeout(displayTimeoutRef.current);
            displayTimeoutRef.current = window.setTimeout(() => {
                setActiveEvent(null);
            }, 5000);
        }, socialProof.intervalMin, socialProof.intervalMax);

        return () => {
            unsubscribe();
            if (displayTimeoutRef.current) clearTimeout(displayTimeoutRef.current);
        };
    }, [addNotification, socialProof.enabled, socialProof.intervalMin, socialProof.intervalMax]);

    const getPositionClasses = () => {
        switch(socialProof.position) {
            case 'top-left': return 'top-20 left-4';
            case 'top-right': return 'top-20 right-4';
            case 'bottom-right': return 'bottom-24 right-4';
            default: return 'bottom-24 left-4';
        }
    };

    const NotificationUI = activeEvent && (
        <div className={`fixed ${getPositionClasses()} z-[70] animate-slide-up pointer-events-none ${socialProof.displayMode === 'outside' ? 'w-full max-w-[280px]' : ''}`}>
            <div className="bg-surface-light/95 dark:bg-[#1c1917]/95 backdrop-blur-xl border border-primary/20 dark:border-white/10 p-3 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] flex items-center gap-3 max-w-[280px] pointer-events-auto cursor-pointer hover:scale-105 transition-transform mx-auto md:mx-0">
                <div className="relative shrink-0">
                    <div className="w-10 h-10 rounded-lg bg-cover bg-center border border-white/10" style={{backgroundImage: `url("${activeEvent.productImage}")`}}></div>
                    <div className="absolute -bottom-1 -right-1 bg-green-500 w-3 h-3 rounded-full border-2 border-white dark:border-black animate-pulse"></div>
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-text-secondary dark:text-gray-400 leading-tight">
                        <span className="font-bold text-text-main dark:text-white">{activeEvent.user}</span> de {activeEvent.location}
                    </p>
                    <p className="text-xs font-bold text-primary truncate leading-tight">
                        {activeEvent.action} {activeEvent.productName}
                    </p>
                    <p className="text-[9px] text-gray-400 mt-0.5 flex items-center gap-1">
                        <span className="material-symbols-outlined text-[10px] filled">schedule</span>
                        {activeEvent.timeAgo}
                    </p>
                </div>
            </div>
        </div>
    );

    return (
        <SocialProofContext.Provider value={null}>
            {children}
            
            {socialProof.enabled && activeEvent && (
                socialProof.displayMode === 'outside' 
                    ? createPortal(NotificationUI, document.body)
                    : (
                        <div className="absolute inset-0 pointer-events-none z-[70] overflow-hidden">
                            {NotificationUI}
                        </div>
                    )
            )}
        </SocialProofContext.Provider>
    );
};
